import { css, cx } from "@emotion/css";
import { Fade, SwipeableDrawer } from "@mui/material";
import { ChevronsLeftIcon } from "@octopusdeploy/design-system-icons";
import { themeTokens, space, borderRadius, text } from "@octopusdeploy/design-system-tokens";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useOnIsHoveredChanged } from "../../hooks";
import { useIsLargerThanIpadResolution } from "../../hooks/breakpoints";
import { useIsUrlActive } from "../../routing";
import { isHTMLElement } from "../../utils";
import type { BadgeProps } from "../Badge/Badge";
import type { NavigationBarActionData } from "../NavigationBar/NavigationBarActions";
import { ScrollArea } from "../ScrollArea";
import { AreaNavList } from "./AreaNavList";
import type { AreaNavListItem, AreaNavListLinkItem } from "./AreaNavList";
import type { SideNavBarTabLink, BottomSideNavBarItem, TopSideNavBarItem } from "./SideNavBar";
import { SideNavBar } from "./SideNavBar";
import { TopNavBar } from "./TopNavBar";
// These z-indexes are local to GlobalLayout through use of isolation css
const zIndexGlobalLayoutContent = 1;
const zIndexAreaNavPanel = 2;
export interface GlobalLayoutProps {
    topNavBar: {
        logo?: React.ReactElement;
        searchBar?: React.ReactElement;
        banner?: React.ReactElement;
        actions?: NavigationBarActionData[];
    };
    sideNavBar: {
        topItems: ReadonlyArray<TopSideNavBarItem>;
        bottomItems: ReadonlyArray<BottomSideNavBarItem>;
        onLinkClicked?: (item: SideNavBarTabLink) => void;
    };
    areaNavPanel: {
        items: ReadonlyArray<AreaNavListItem>;
        title?: string;
        badge?: React.ReactElement<BadgeProps>;
        displayMode: "overlay" | "inline";
        onLinkClicked?: (item: AreaNavListLinkItem) => void;
    };
    helpPanel: {
        isExpanded: boolean;
        content: React.ReactElement;
    };
    children: React.ReactNode;
    onSideBarClickAway?: () => void;
    scrollAreaRef: React.RefObject<HTMLDivElement>;
}
export function GlobalLayout({ topNavBar, sideNavBar, areaNavPanel, onSideBarClickAway, children, scrollAreaRef, helpPanel }: GlobalLayoutProps) {
    const isLargerThanIpad = useIsLargerThanIpadResolution();
    const [isSideDrawerExpanded, setIsSideDrawerExpanded] = useState(false);
    const [isAreaNavPanelExpanded, setIsAreaNavPanelExpanded] = useState(getDefaultAreaNavPanelExpandState(areaNavPanel.displayMode));
    const [hoveredSideNavLink, setHoveredSideNavLink] = useState<SideNavBarTabLink | undefined>();
    const [isAreaNavPanelHovered, setIsAreaNavPanelHovered] = useState(false);
    useLayoutEffect(() => {
        setIsAreaNavPanelExpanded(getDefaultAreaNavPanelExpandState(areaNavPanel.displayMode));
    }, [areaNavPanel.displayMode]);
    const isUrlActive = useIsUrlActive();
    const expandSideDrawer = () => setIsSideDrawerExpanded(true);
    const collapseSideDrawer = () => setIsSideDrawerExpanded(false);
    const handleSideNavBarLinkClicked = useCallback((event: React.MouseEvent, item: SideNavBarTabLink) => {
        const isActive = isUrlActive(item.href, item.showLinkAsActive ?? "if path partially matches");
        if (areaNavPanel.displayMode === "inline" && isActive) {
            // If the user is clicking tab because they can't see the AreaNavPanel, toggle
            // the AreaNavPanel to let them peek the menu contents.
            setIsAreaNavPanelExpanded((isExpanded) => !isExpanded);
            event?.preventDefault();
        }
        else {
            // We're navigating to the Deploy area from somewhere else, so we always want to show the nav panel even if
            // it was collapsed in the other area. Expand it then fall through to the regular route/link onClick.
            setIsAreaNavPanelExpanded(true);
        }
        sideNavBar.onLinkClicked?.(item);
        setIsSideDrawerExpanded(false);
    }, [areaNavPanel.displayMode, isUrlActive, sideNavBar]);
    const handleAreaNavPanelLinkClicked = useCallback((item: AreaNavListLinkItem) => {
        areaNavPanel.onLinkClicked?.(item);
        collapseSideDrawer();
    }, [areaNavPanel]);
    useEffect(() => {
        if (!isLargerThanIpad || areaNavPanel.displayMode !== "overlay")
            return;
        const hasActiveHoveredSideNavLink = hoveredSideNavLink && isUrlActive(hoveredSideNavLink.href, hoveredSideNavLink.showLinkAsActive ?? "if path partially matches");
        if (!isAreaNavPanelExpanded && hasActiveHoveredSideNavLink) {
            setIsAreaNavPanelExpanded(true);
            return;
        }
        if (!hasActiveHoveredSideNavLink && !isAreaNavPanelHovered) {
            const collapseTimeout = window.setTimeout(() => {
                setIsAreaNavPanelExpanded(false);
            }, 200);
            return () => {
                window.clearTimeout(collapseTimeout);
            };
        }
    }, [areaNavPanel.displayMode, hoveredSideNavLink, isAreaNavPanelHovered, isLargerThanIpad, isUrlActive, isAreaNavPanelExpanded]);
    const mobileSideContainer = (<GlobalLayoutSideContainerDrawer isExpanded={isSideDrawerExpanded} onClose={collapseSideDrawer} onOpen={expandSideDrawer}>
            <SideNavBar topItems={sideNavBar.topItems} bottomItems={sideNavBar.bottomItems} onLinkClicked={handleSideNavBarLinkClicked} onHoveredLinkChanged={setHoveredSideNavLink}/>
            <AreaNavPanel isCollapsible={false} listItems={areaNavPanel.items} title={areaNavPanel.title} badge={areaNavPanel.badge} onLinkClicked={handleAreaNavPanelLinkClicked}/>
        </GlobalLayoutSideContainerDrawer>);
    const sideContainer = (<GlobalLayoutSideContainerFixed onClickAway={onSideBarClickAway}>
            <SideNavBar topItems={sideNavBar.topItems} bottomItems={sideNavBar.bottomItems} onLinkClicked={handleSideNavBarLinkClicked} onHoveredLinkChanged={setHoveredSideNavLink}/>
            <AreaNavPanel isCollapsible={true} listItems={areaNavPanel.items} title={areaNavPanel.title} badge={areaNavPanel.badge} isExpanded={isAreaNavPanelExpanded} setIsExpanded={setIsAreaNavPanelExpanded} onLinkClicked={handleAreaNavPanelLinkClicked} displayMode={areaNavPanel.displayMode} onIsHoveredChanged={setIsAreaNavPanelHovered}/>
        </GlobalLayoutSideContainerFixed>);
    return (<div className={rootProps}>
            <TopNavBar {...topNavBar} onExpandMenu={expandSideDrawer}/>
            <div className={mainContainerProps}>
                {isLargerThanIpad ? sideContainer : mobileSideContainer}
                <GlobalLayoutContent isAreaNavPanelExpandedInline={isAreaNavPanelExpanded && areaNavPanel.displayMode === "inline" && areaNavPanel.items.length > 0} isHelpPanelVisible={helpPanel.isExpanded} scrollAreaRef={scrollAreaRef}>
                    {children}
                </GlobalLayoutContent>
                {isLargerThanIpad && helpPanel.isExpanded && <GlobalLayoutHelpPanel>{helpPanel.content}</GlobalLayoutHelpPanel>}
            </div>
        </div>);
}
function getDefaultAreaNavPanelExpandState(displayMode: "overlay" | "inline"): boolean {
    return displayMode === "inline";
}
type GlobalLayoutSideContainerFixedProps = {
    onClickAway?: () => void;
    children: React.ReactNode;
};
type GlobalLayoutSideContainerDrawerProps = {
    isExpanded: boolean;
    onClose: () => void;
    onOpen: () => void;
    children: React.ReactNode;
};
function GlobalLayoutSideContainerFixed({ onClickAway, children }: React.PropsWithChildren<GlobalLayoutSideContainerFixedProps>) {
    const containerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const container = containerRef.current;
        if (container === null)
            return;
        const onPointerDown = (event: FocusEvent | PointerEvent) => {
            if (isHTMLElement(event.target) && !container.contains(event.target) && onClickAway) {
                onClickAway();
            }
        };
        window.addEventListener("pointerdown", onPointerDown);
        return () => {
            window.removeEventListener("pointerdown", onPointerDown);
        };
    }, [onClickAway]);
    return (<div ref={containerRef} className={globalLayoutSideContainerFixedStyles}>
            {children}
        </div>);
}
function GlobalLayoutSideContainerDrawer({ isExpanded, onOpen, onClose, children }: React.PropsWithChildren<GlobalLayoutSideContainerDrawerProps>) {
    return (<SwipeableDrawer open={isExpanded} onClose={onClose} onOpen={onOpen}>
            <div className={globalLayoutSideContainerDrawerStyles}>{children}</div>
        </SwipeableDrawer>);
}
const globalLayoutSideContainerFixedStyles = css({
    display: "flex",
    position: "relative", // Needed when AreaNavBar goes into overlay/absolute mode to allow scrolling.
});
const globalLayoutSideContainerDrawerStyles = css({
    display: "flex",
    backgroundColor: themeTokens.color.background.secondary.default,
    height: "100%",
});
type AreaNavPanelProps = {
    listItems: ReadonlyArray<AreaNavListItem>;
    title?: string;
    badge?: React.ReactElement<BadgeProps>;
    onLinkClicked?: (item: AreaNavListLinkItem) => void;
} & ({
    isCollapsible: false;
} | {
    isCollapsible: true;
    isExpanded: boolean;
    setIsExpanded: (isExpanded: boolean) => void;
    onIsHoveredChanged: (isHovered: boolean) => void;
    displayMode: "overlay" | "inline";
});
function AreaNavPanel(props: AreaNavPanelProps) {
    const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);
    useOnIsHoveredChanged(containerElement, props.isCollapsible ? props.onIsHoveredChanged : undefined);
    if (props.listItems.length === 0) {
        return null;
    }
    if (!props.isCollapsible) {
        return (
        // adora-unmask is temporary for Adora trial - #team-growth-experience-requests-and-discussion
        <div className={cx(areaNavPanelStyles, areaNavPanelRelativeStyles, "adora-unmask")}>
                {props.title && <div className={areaNavPanelTitleStyles}>{props.title}</div>}
                {props.badge && <div className={areaNavPanelBadgeStyles}>{props.badge}</div>}
                <AreaNavList listItems={props.listItems} onLinkClicked={props.onLinkClicked} hasTitle={!!props.title}/>
            </div>);
    }
    const { displayMode, isExpanded, setIsExpanded } = props;
    if (displayMode === "inline") {
        return (
        // adora-unmask is temporary for Adora trial - #team-growth-experience-requests-and-discussion
        <div ref={setContainerElement} className={cx(areaNavPanelStyles, areaNavPanelRelativeStyles, "adora-unmask")}>
                <AreaNavPanelCollapsedToggle isPanelExpanded={isExpanded} onToggle={() => setIsExpanded(!isExpanded)}/>
                {isExpanded && (<>
                        {props.title && <div className={areaNavPanelTitleStyles}>{props.title}</div>}
                        {props.badge && <div className={areaNavPanelBadgeStyles}>{props.badge}</div>}
                        <AreaNavList listItems={props.listItems} onLinkClicked={props.onLinkClicked} hasTitle={!!props.title}/>
                    </>)}
            </div>);
    }
    return (<Fade ref={setContainerElement} in={isExpanded}>
            {/* adora-unmask is temporary for Adora trial - #team-growth-experience-requests-and-discussion */}
            <div className={cx(areaNavPanelStyles, areaNavPanelAbsoluteStyles, "adora-unmask")}>
                {props.title && <div className={areaNavPanelTitleStyles}>{props.title}</div>}
                {props.badge && <div className={areaNavPanelBadgeStyles}>{props.badge}</div>}
                <AreaNavList listItems={props.listItems} onLinkClicked={props.onLinkClicked} hasTitle={!!props.title}/>
            </div>
        </Fade>);
}
const toggleAreaNavPanelId = "toggleAreaNavPanel";
const areaNavPanelStyles = css({
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    boxShadow: themeTokens.shadow.layoutPrimary,
    borderTopLeftRadius: borderRadius.medium,
    overflow: "hidden",
    "&::-webkit-scrollbar": {
        width: 0,
        height: 0,
    },
    [`:hover #${toggleAreaNavPanelId}`]: {
        opacity: "0.4",
        visibility: "visible",
    },
});
const areaNavPanelAbsoluteStyles = css({
    position: "absolute",
    left: "4.25rem",
    height: "100%",
    boxShadow: themeTokens.shadow.large,
    zIndex: zIndexAreaNavPanel, // This needs to show above the global layout content
    // Need a solid background when we're in absolute mode since we're overlaying other content.
    backgroundColor: themeTokens.color.background.primary.default,
});
const areaNavPanelRelativeStyles = css({
    position: "relative",
});
const areaNavPanelTitleStyles = css({
    font: text.heading.large,
    padding: `${space[32]} ${space[16]} 0 ${space[16]}`,
    background: themeTokens.color.background.primary.transparent,
});
const areaNavPanelBadgeStyles = css({
    padding: `${space[16]} ${space[16]} 0 ${space[16]}`,
    background: themeTokens.color.background.primary.transparent,
});
interface GlobalLayoutContentProps {
    isAreaNavPanelExpandedInline: boolean;
    isHelpPanelVisible: boolean;
    scrollAreaRef: React.RefObject<HTMLDivElement | null>;
    children: React.ReactNode;
}
function GlobalLayoutContent({ isAreaNavPanelExpandedInline, isHelpPanelVisible, scrollAreaRef, children }: GlobalLayoutContentProps) {
    return (<main className={cx(globalLayoutContentStyles, {
            [globalLayoutContentWithoutAreaNavPanelStyles]: !isAreaNavPanelExpandedInline,
            [globalLayoutContentWithHelpPanelStyles]: isHelpPanelVisible,
        })}>
            <ScrollArea scrollAreaRef={scrollAreaRef} stableGutter={true}>
                {children}
            </ScrollArea>
        </main>);
}
interface AreaNavPanelCollapsedToggleProps {
    isPanelExpanded: boolean;
    onToggle: () => void;
}
function AreaNavPanelCollapsedToggle({ isPanelExpanded, onToggle }: AreaNavPanelCollapsedToggleProps) {
    const handleToggle = React.useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        onToggle();
    }, [onToggle]);
    return (<div id={toggleAreaNavPanelId} className={toggleStyles} onClick={handleToggle}>
            {isPanelExpanded && <ChevronsLeftIcon size={16}/>}
        </div>);
}
const toggleStyles = css({
    position: "absolute",
    opacity: "0",
    visibility: "hidden",
    transition: "opacity 300ms",
    transitionTimingFunction: "ease-in",
    right: space[16],
    top: space[12],
    cursor: "pointer",
    ".MuiIconButton-root": {
        color: themeTokens.color.icon.secondary,
    },
});
function GlobalLayoutHelpPanel({ children }: {
    children: React.ReactNode;
}) {
    return <div className={globalLayoutHelpPanelStyles}>{children}</div>;
}
const globalLayoutHelpPanelStyles = css({
    position: "relative",
    display: "flex",
    boxShadow: themeTokens.shadow.layoutPrimary,
    background: themeTokens.color.background.primary.default,
    marginLeft: space[16],
    borderTopLeftRadius: borderRadius.medium,
});
const globalLayoutContentStyles = css({
    flex: 1,
    isolation: "isolate",
    boxShadow: themeTokens.shadow.layoutPrimary,
    background: themeTokens.color.background.primary.default,
    overflow: "clip",
    // Prevents content from overflowing the width of this container
    minWidth: 0,
    // There is some content in portal that shows a fullscreen dialog/overlay e.g. the new project dialog
    // Without this z-index, the help panel has higher natural stacking order and appears over this content
    zIndex: zIndexGlobalLayoutContent,
});
const globalLayoutContentWithoutAreaNavPanelStyles = css({
    borderTopLeftRadius: borderRadius.medium,
});
const globalLayoutContentWithHelpPanelStyles = css({
    borderTopRightRadius: borderRadius.medium,
});
const rootProps = css({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    backgroundColor: themeTokens.color.background.secondary.default,
});
const mainContainerProps = css({
    flex: 1,
    display: "flex",
    minHeight: 0,
    isolation: "isolate",
});
